import { useState, useEffect, useRef, forwardRef } from 'react';
import { axiosApi } from '@/config';

interface AvatarImageProps {
	subcontractId?: number;
	name?: string;
	className?: string;
	size?: 'sm' | 'md' | 'lg';
}

// Cache simple para las imágenes
const imageCache = new Map<string, string>();

const AvatarImage = forwardRef<HTMLDivElement, AvatarImageProps>(
	(
		{ subcontractId, name, className = 'w-8 h-8 object-cover rounded-full', size = 'md' },
		ref,
	) => {
		const [imageSrc, setImageSrc] = useState<string | null>(null);
		const [hasError, setHasError] = useState(false);
		const [isLoading, setIsLoading] = useState(true);
		const abortControllerRef = useRef<AbortController | null>(null);

		useEffect(() => {
			if (!subcontractId) {
				setIsLoading(false);
				setHasError(true);
				return;
			}

			// Clave de caché estable por subcontrata: sin `Date.now()`, que la invalidaba
			// en cada render y provocaba una petición HTTP por fila y por re-render.
			const url = `${axiosApi.defaults.baseURL}/subcontract/${subcontractId}/avatar`;

			// Verificar si la imagen ya está en cache
			if (imageCache.has(url)) {
				setImageSrc(imageCache.get(url)!);
				setIsLoading(false);
				setHasError(false);
				return;
			}

			// Crear un AbortController para cancelar peticiones previas
			abortControllerRef.current = new AbortController();

			const img = new Image();
			img.crossOrigin = 'anonymous';

			img.onload = () => {
				// Cachear la imagen una vez cargada
				imageCache.set(url, url);
				setImageSrc(url);
				setIsLoading(false);
				setHasError(false);
			};

			img.onerror = () => {
				setHasError(true);
				setIsLoading(false);
			};

			img.src = url;

			// Cleanup function
			return () => {
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}
			};
		}, [subcontractId]);

		// Determinar el tamaño de la imagen según la prop size
		const getSizeClasses = () => {
			switch (size) {
				case 'sm':
					return 'w-6 h-6';
				case 'lg':
					return 'w-12 h-12';
				default:
					return 'w-8 h-8';
			}
		};

		if (isLoading) {
			return (
				<div
					ref={ref}
					className={`${getSizeClasses()} bg-gray-200 animate-pulse rounded-full ${className}`}
				/>
			);
		}

		if (hasError || !imageSrc) {
			return (
				<div
					ref={ref}
					className={`${getSizeClasses()} bg-gray-300 rounded-full flex items-center justify-center ${className}`}>
					<span className="text-gray-600 text-xs font-medium">
						{name?.charAt(0)?.toUpperCase() || '?'}
					</span>
				</div>
			);
		}

		return (
			<div ref={ref} className="inline-block">
				<img
					src={imageSrc}
					alt={name || 'Avatar'}
					className={`${getSizeClasses()} object-cover rounded-full ${className}`}
					onError={() => setHasError(true)}
				/>
			</div>
		);
	},
);

AvatarImage.displayName = 'AvatarImage';

export default AvatarImage;
